// Exception-based oversight (v3 §Chunk 5). Turn the ledger into a ranked list of things
// that breach the active materiality rules, so oversight reviews by exception, not line-by-line.

import { assuranceOf } from "./hooks";
import type { WorkspaceSeed, MaterialityConfig, LeakCategory } from "./types";
import type { TraceTarget } from "./trace";

export type ExceptionSeverity = "high" | "medium" | "low";

export type Exception = {
  id: string;
  ruleKey: string;
  ruleLabel: string;
  severity: ExceptionSeverity;
  title: string;
  body?: string;
  figure?: number;
  target?: TraceTarget;
  spendId?: string;
  projectId?: string;
  state?: "open" | "investigating" | "resolved" | "not_a_leak";
};

const LEAK_RULE: Record<LeakCategory, { label: string; severity: ExceptionSeverity }> = {
  threshold_split: { label: "Threshold split", severity: "high" },
  suspected_duplicate: { label: "Suspected duplicate", severity: "high" },
  anomalous_supplier: { label: "Anomalous supplier", severity: "medium" },
  missing_evidence: { label: "Missing evidence", severity: "medium" },
  timing_variance: { label: "Timing variance", severity: "low" },
};

const SEV_RANK: Record<ExceptionSeverity, number> = { high: 0, medium: 1, low: 2 };

export function deriveExceptions(seed: WorkspaceSeed, cfg: MaterialityConfig): Exception[] {
  const out: Exception[] = [];
  const covered = new Set<string>();

  // 1. Reconciliation-engine leaks that are still live.
  for (const l of seed.leaks) {
    if (l.state === "resolved" || l.state === "not_a_leak") continue;
    const meta = LEAK_RULE[l.category];
    const spendId = l.contributingItemIds.find((id) => id.startsWith("sr-"));
    if (spendId) covered.add(spendId);
    out.push({
      id: `exc-leak-${l.id}`,
      ruleKey: l.category,
      ruleLabel: meta.label,
      severity: meta.severity,
      title: l.title,
      body: l.body,
      figure: l.amount,
      projectId: l.projectId,
      spendId,
      target: spendId ? { kind: "spend", id: spendId } : { kind: "project", id: l.projectId },
      state: l.state,
    });
  }

  // 2. Spend-level rule breaches.
  const moneyOut = (s: string) => s === "committed" || s === "evidence_submitted" || s === "confirmed";
  for (const r of seed.spendRequests) {
    if (covered.has(r.id)) continue;
    const level = assuranceOf(r, seed.evidence ?? [], seed.spendItems);
    if (moneyOut(r.state) && level === "self_reported" && r.amount >= cfg.gapValue && !r.gapExplanation) {
      out.push({
        id: `exc-gap-${r.id}`,
        ruleKey: "self_reported_gap",
        ruleLabel: "Unexplained self-reported spend",
        severity: "high",
        title: r.title,
        body: "Money is out but the figure is only self-reported, above the materiality threshold, with no explanation.",
        figure: r.amount,
        spendId: r.id,
        projectId: r.projectId,
        target: { kind: "spend", id: r.id },
      });
      continue;
    }
    const reviewable = r.state !== "rejected";
    if (reviewable && r.amount >= cfg.spendReview && !r.verifiedById) {
      out.push({
        id: `exc-large-${r.id}`,
        ruleKey: "large_spend",
        ruleLabel: "Large spend, unverified",
        severity: r.amount >= cfg.spendReview * 2 ? "high" : "medium",
        title: r.title,
        body: "Above the review threshold and not yet independently verified.",
        figure: r.amount,
        spendId: r.id,
        projectId: r.projectId,
        target: { kind: "spend", id: r.id },
      });
    }
  }

  // 3. Project-level rule breaches.
  for (const p of seed.projects) {
    const used = p.budgeted ? p.spent / p.budgeted : 0;
    if (used >= cfg.budgetUsed) {
      out.push({
        id: `exc-budget-${p.id}`,
        ruleKey: "budget_ceiling",
        ruleLabel: "Near budget ceiling",
        severity: "high",
        title: p.name,
        body: `${Math.round(used * 100)}% of budget consumed.`,
        figure: p.spent,
        projectId: p.id,
        target: { kind: "project", id: p.id },
      });
    }
    if ((p.scheduleDriftDays ?? 0) > cfg.driftDays) {
      out.push({
        id: `exc-drift-${p.id}`,
        ruleKey: "schedule_drift",
        ruleLabel: "Schedule drift",
        severity: "medium",
        title: p.name,
        body: `Running ${p.scheduleDriftDays}d behind plan.`,
        projectId: p.id,
        target: { kind: "project", id: p.id },
      });
    }
  }

  return out.sort((a, b) => SEV_RANK[a.severity] - SEV_RANK[b.severity] || (b.figure ?? 0) - (a.figure ?? 0));
}

/** A risk-weighted verification sample: the highest-value money-out spend not yet verified. */
export function pickVerificationSample(seed: WorkspaceSeed, n: number): WorkspaceSeed["spendRequests"] {
  return seed.spendRequests
    .filter((r) => (r.state === "committed" || r.state === "confirmed" || r.state === "evidence_submitted") && !r.verifiedById)
    .sort((a, b) => b.amount - a.amount)
    .slice(0, n);
}
